import React, { useState } from 'react';
import { X, Save, Loader2, KeyRound, CheckCircle2, AlertTriangle, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { supabase } from '@/supabaseClient';
import { markPasswordAsChanged } from '@/services/database';

interface ChangePasswordModalProps {
  onClose: () => void;
  forceChange?: boolean;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onClose, forceChange = false }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("A nova senha e a confirmação não coincidem.");
      return;
    }

    if (newPassword.length < 6) {
      setError("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const userEmail = sessionData.session?.user?.email;
    const userId = sessionData.session?.user?.id;
    if (!userEmail || !userId) {
      setError("Erro de sessão. Por favor, faça login novamente.");
      return;
    }

    setIsLoading(true);

    try {
      // 1. Re-authenticate user to ensure security if NOT a forced password change
      if (!forceChange) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: userEmail,
          password: currentPassword
        });
        if (signInError) {
          throw new Error("A senha atual está incorreta.");
        }
      }

      // 2. Update Password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (updateError) throw updateError;
      
      // 3. Mark as changed in Database if forced
      if (forceChange) {
         await markPasswordAsChanged(userId);
      }

      setSuccess(true);
      
      // Clear forms
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Auto close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err: any) {
      console.error("Password change error:", err);
      if (err.message === "A senha atual está incorreta.") {
        setError("A senha atual está incorreta.");
      } else if (err.status === 429 || err.message?.includes("too many requests")) {
        setError("Muitas tentativas. Aguarde um momento.");
      } else {
        setError("Erro ao atualizar senha: " + err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold flex items-center gap-2 text-slate-800">
            <KeyRound className="w-5 h-5 text-blue-600" />
            {forceChange ? 'Definir Nova Senha' : 'Alterar Senha'}
          </h3>
          {!forceChange && ( // Don't show close button if forceChange is true
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Body */}
        {success ? (
          <div className="text-center p-8 bg-green-50 border border-green-100 rounded-b-xl animate-fade-in">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h4 className="text-2xl font-bold text-green-800 mb-2">Senha Atualizada!</h4>
            <p className="text-green-700 text-lg">Sua senha foi alterada com sucesso.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {forceChange && (
              <div className="bg-orange-50 border border-orange-100 text-orange-700 p-3 rounded-lg text-sm flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Você deve alterar sua senha antes de continuar.</span>
              </div>
            )}
            {!forceChange && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Senha Atual</label>
                <div className="relative">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    required
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none pr-10"
                    placeholder="Sua senha atual"
                  />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600">
                    {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nova Senha</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  required
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none pr-10"
                  placeholder="Mínimo 6 caracteres"
                  minLength={6}
                />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600">
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirmar Nova Senha</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Confirme a nova senha"
                minLength={6}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2 border border-red-100 animate-fade-in">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="pt-4 flex justify-end gap-3">
              <button
                type="submit"
                disabled={isLoading}
                className="px-5 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {forceChange ? 'Definir Senha' : 'Salvar Senha'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};